import React, { useState } from 'react';
import { Box, Button, TextField, Select, MenuItem, IconButton, Chip, FormControl, InputLabel } from '@mui/material';
import { Plus, Trash2, X } from 'lucide-react';

const OPERATORS_BY_TYPE = {
  string: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS', 'STARTS_WITH', 'ENDS_WITH', 'IN', 'NOT_IN', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_OR_EQUAL', 'LESS_OR_EQUAL', 'BETWEEN'],
  datetime: ['EQUALS', 'NOT_EQUALS', 'BEFORE', 'AFTER', 'BETWEEN'],
  boolean: ['EQUALS'],
  any: ['EXISTS', 'NOT_EXISTS'],
};

const OPERATOR_LABELS = {
  EQUALS: 'Igual a', NOT_EQUALS: 'Diferente de', CONTAINS: 'Contém', NOT_CONTAINS: 'Não contém',
  STARTS_WITH: 'Começa com', ENDS_WITH: 'Termina com', IN: 'Está em', NOT_IN: 'Não está em',
  IS_EMPTY: 'Está vazio', IS_NOT_EMPTY: 'Não está vazio', GREATER_THAN: 'Maior que', LESS_THAN: 'Menor que',
  GREATER_OR_EQUAL: 'Maior ou igual', LESS_OR_EQUAL: 'Menor ou igual', BETWEEN: 'Entre',
  BEFORE: 'Antes de', AFTER: 'Depois de', EXISTS: 'Existe', NOT_EXISTS: 'Não existe',
};

const DATA_TYPES = [
  { value: 'string', label: 'Texto' }, { value: 'number', label: 'Número' },
  { value: 'datetime', label: 'Data/Hora' }, { value: 'boolean', label: 'Sim/Não' }, { value: 'any', label: 'Qualquer' },
];

const NO_VALUE_OPERATORS = ['IS_EMPTY', 'IS_NOT_EMPTY', 'EXISTS', 'NOT_EXISTS'];

const emptyCondition = () => ({ field_path: '', data_type: 'string', operator: 'EQUALS', value: '' });

const ConditionBuilder = ({ value, onChange, providerFields = [] }) => {
  const parseCondition = (json) => {
    if (!json) return null;
    try { return typeof json === 'string' ? JSON.parse(json) : json; } catch { return null; }
  };

  const [enabled, setEnabled] = useState(!!value);
  const [condition, setCondition] = useState(parseCondition(value) || { logical_operator: 'AND', conditions: [emptyCondition()] });

  const handleChange = (newCondition) => {
    setCondition(newCondition);
    if (onChange) onChange(JSON.stringify(newCondition));
  };

  const toggleEnabled = () => {
    if (enabled) {
      setEnabled(false);
      if (onChange) onChange(null);
    } else {
      setEnabled(true);
      const initial = { logical_operator: 'AND', conditions: [emptyCondition()] };
      setCondition(initial);
      if (onChange) onChange(JSON.stringify(initial));
    }
  };

  const renderConditionGroup = (group, path = []) => {
    const isRoot = path.length === 0;
    const logicalOp = group.logical_operator || 'AND';

    return (
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, mb: 1, background: isRoot ? '#fafafa' : '#f5f5f5' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={logicalOp} size="small" color={logicalOp === 'AND' ? 'primary' : 'secondary'}
              onClick={() => {
                const newGroup = { ...group, logical_operator: logicalOp === 'AND' ? 'OR' : 'AND' };
                updateGroupAtPath(newGroup, path);
              }} sx={{ cursor: 'pointer' }} />
            <span style={{ fontSize: '11px', color: '#999' }}>{logicalOp === 'AND' ? 'Todas as condições' : 'Qualquer condição'}</span>
          </Box>
          {!isRoot && (
            <IconButton size="small" onClick={() => removeGroupAtPath(path)}><X size={14} /></IconButton>
          )}
        </Box>

        {(group.conditions || []).map((cond, idx) => {
          if (cond.logical_operator) {
            return <div key={idx}>{renderConditionGroup(cond, [...path, idx])}</div>;
          }
          return (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField select size="small" label="Campo" value={cond.field_path || ''} sx={{ minWidth: 200 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                onChange={e => updateConditionField(path, idx, 'field_path', e.target.value)}>
                <option value="">Selecione...</option>
                {providerFields.map(f => <option key={f.id} value={f.fieldPath}>{f.fieldName}</option>)}
              </TextField>

              <TextField select size="small" label="Tipo" value={cond.data_type || 'string'} sx={{ minWidth: 100 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                onChange={e => updateConditionField(path, idx, 'data_type', e.target.value)}>
                {DATA_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
              </TextField>

              <TextField select size="small" label="Operador" value={cond.operator || 'EQUALS'} sx={{ minWidth: 140 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                onChange={e => updateConditionField(path, idx, 'operator', e.target.value)}>
                {(OPERATORS_BY_TYPE[cond.data_type || 'string'] || []).map(op => (
                  <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>
                ))}
              </TextField>

              {!NO_VALUE_OPERATORS.includes(cond.operator) && (
                <TextField size="small" label="Valor" value={cond.value || ''} sx={{ minWidth: 140 }}
                  onChange={e => updateConditionField(path, idx, 'value', e.target.value)} />
              )}

              <IconButton size="small" onClick={() => removeConditionAtPath(path, idx)}><Trash2 size={14} color="#c62828" /></IconButton>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" startIcon={<Plus size={14} />} onClick={() => addConditionAtPath(path)} sx={{ textTransform: 'none', fontSize: '12px' }}>
            Regra
          </Button>
          <Button size="small" variant="outlined" onClick={() => addGroupAtPath(path, 'AND')} sx={{ textTransform: 'none', fontSize: '12px' }}>
            + Grupo AND
          </Button>
          <Button size="small" variant="outlined" onClick={() => addGroupAtPath(path, 'OR')} sx={{ textTransform: 'none', fontSize: '12px' }}>
            + Grupo OR
          </Button>
        </Box>
      </Box>
    );
  };

  const getGroupAtPath = (path) => {
    let current = condition;
    for (const idx of path) current = current.conditions[idx];
    return current;
  };

  const updateGroupAtPath = (newGroup, path) => {
    if (path.length === 0) { handleChange(newGroup); return; }
    const newCondition = JSON.parse(JSON.stringify(condition));
    let parent = newCondition;
    for (let i = 0; i < path.length - 1; i++) parent = parent.conditions[path[i]];
    parent.conditions[path[path.length - 1]] = newGroup;
    handleChange(newCondition);
  };

  const updateConditionField = (groupPath, condIdx, field, value) => {
    const newCondition = JSON.parse(JSON.stringify(condition));
    let group = newCondition;
    for (const idx of groupPath) group = group.conditions[idx];
    group.conditions[condIdx] = { ...group.conditions[condIdx], [field]: value };
    handleChange(newCondition);
  };

  const addConditionAtPath = (groupPath) => {
    const newCondition = JSON.parse(JSON.stringify(condition));
    let group = newCondition;
    for (const idx of groupPath) group = group.conditions[idx];
    group.conditions.push(emptyCondition());
    handleChange(newCondition);
  };

  const removeConditionAtPath = (groupPath, condIdx) => {
    const newCondition = JSON.parse(JSON.stringify(condition));
    let group = newCondition;
    for (const idx of groupPath) group = group.conditions[idx];
    group.conditions.splice(condIdx, 1);
    handleChange(newCondition);
  };

  const addGroupAtPath = (groupPath, op) => {
    const newCondition = JSON.parse(JSON.stringify(condition));
    let group = newCondition;
    for (const idx of groupPath) group = group.conditions[idx];
    group.conditions.push({ logical_operator: op, conditions: [emptyCondition()] });
    handleChange(newCondition);
  };

  const removeGroupAtPath = (path) => {
    if (path.length === 0) return;
    const newCondition = JSON.parse(JSON.stringify(condition));
    let parent = newCondition;
    for (let i = 0; i < path.length - 1; i++) parent = parent.conditions[path[i]];
    parent.conditions.splice(path[path.length - 1], 1);
    handleChange(newCondition);
  };

  return (
    <Box>
      <Button size="small" variant={enabled ? 'contained' : 'outlined'} onClick={toggleEnabled}
        sx={{ textTransform: 'none', mb: 1 }}>
        {enabled ? 'Condição ativa' : 'Adicionar condição de execução'}
      </Button>
      {enabled && renderConditionGroup(condition)}
      {!enabled && <p style={{ fontSize: '12px', color: '#999' }}>Sem condição = trigger sempre executa</p>}
    </Box>
  );
};

export default ConditionBuilder;
